import { useCallback } from 'react'
import {
  cartIdSelector,
  resolveCatchMessage,
  TCatchMessage,
  updateCartCoupon,
  updateCartPrices,
  useToastContext,
} from '@segway/core'
import { ApplyCouponToCartInput } from '@segway/core/src/graphql/generated/graphql'
import {
  useApplyCouponToCartMutation,
  useRemoveCouponFromCartMutation,
} from '@segway/core/src/services/client/cartApi'
import { useAppDispatch, useAppSelector } from '@segway/core/src/store/hooks'

/**
 * Coupon code Hooks
 */
const useCouponCode = () => {
  const dispatch = useAppDispatch()
  const toast = useToastContext()
  const cartId = useAppSelector(cartIdSelector)
  const [applyCouponToCartMutation, { isLoading: applyCouponLoading }] =
    useApplyCouponToCartMutation()
  const [removeCouponFromCartMutation, { isLoading: removeCouponLoading }] =
    useRemoveCouponFromCartMutation()

  /**
   * Apply coupon code
   */
  const applyCouponToCart = useCallback(
    async (coupon_code: ApplyCouponToCartInput['coupon_code']) => {
      try {
        const res = await applyCouponToCartMutation({
          input: {
            cart_id: cartId,
            coupon_code,
          },
        }).unwrap()

        if (res?.applyCouponToCart?.cart) {
          dispatch(updateCartPrices(res.applyCouponToCart.cart.prices))
          dispatch(updateCartCoupon(res.applyCouponToCart.cart.applied_coupons))
          return res.applyCouponToCart.cart
        }

        return null
      } catch (error) {
        toast.show({
          icon: 'fail',
          content: resolveCatchMessage(error as TCatchMessage),
        })
        return null
      }
    },
    [applyCouponToCartMutation, cartId, dispatch, toast],
  )

  /**
   * Cancel coupon code
   */
  const removeCouponFromCart = useCallback(async () => {
    try {
      const res = await removeCouponFromCartMutation({
        input: {
          cart_id: cartId,
        },
      }).unwrap()

      if (res?.removeCouponFromCart?.cart) {
        dispatch(updateCartPrices(res.removeCouponFromCart.cart.prices))
        dispatch(updateCartCoupon(res.removeCouponFromCart.cart.applied_coupons))
        return res.removeCouponFromCart.cart
      }

      return null
    } catch (error) {
      toast.show({
        icon: 'fail',
        content: resolveCatchMessage(error as TCatchMessage),
      })
      return null
    }
  }, [removeCouponFromCartMutation, cartId, dispatch, toast])

  return { applyCouponToCart, removeCouponFromCart, applyCouponLoading, removeCouponLoading }
}

export default useCouponCode
